// ============================================================================
// OcrCaptureModal — Personal Finance V1.0 (17/07/2026)
// Modal de captura de prints (extratos, faturas, comprovantes) via OCR.
// Fluxo: Upload imagem -> OCR -> Preview dados -> Enviar para BatchImportPanel
// ============================================================================

import React, { useState, useRef, useCallback } from "react";
import {
  extractTextFromImage,
  parseOcrText,
  toBatchRows,
} from "../services/ocrService.js";

export function OcrCaptureModal({ onClose, onRowsExtracted }) {
  const [imagePreview, setImagePreview] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(null);
  const [ocrStatus, setOcrStatus] = useState("idle"); // idle | processing | parsed | error
  const [ocrRawText, setOcrRawText] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    setErrorMsg("");
    setOcrStatus("idle");
    setOcrRawText("");
    setParsedRows([]);
    setOcrProgress(null);

    // Preview da imagem
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type.startsWith("image/")) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleRunOcr = useCallback(async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setErrorMsg("Selecione ou arraste uma imagem primeiro.");
      return;
    }

    setOcrStatus("processing");
    setOcrProgress(0);
    setErrorMsg("");

    try {
      const rawText = await extractTextFromImage(file, {
        lang: "por",
        onProgress: (p) => setOcrProgress(p),
      });

      setOcrRawText(rawText);

      const parsed = parseOcrText(rawText);
      setParsedRows(parsed);
      setOcrStatus(parsed.length > 0 ? "parsed" : "parsed");
    } catch (err) {
      setOcrStatus("error");
      setErrorMsg("Erro ao processar OCR: " + (err.message || err));
    }
  }, []);

  const handleSendToBatch = useCallback(() => {
    if (parsedRows.length === 0) return;
    const batchRows = toBatchRows(parsedRows);
    onRowsExtracted(batchRows);
    onClose();
  }, [parsedRows, onRowsExtracted, onClose]);

  const handleInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      handleFileSelect(file);
    },
    [handleFileSelect],
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-[640px] max-h-[85vh] flex flex-col overflow-hidden border-t-4 border-[#C5A059]">
        {/* HEADER */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
          <h4 className="text-[13px] font-bold text-[#20243B] flex items-center gap-2">
            <i className="fas fa-camera text-[#C5A059]" />
            Captura por OCR
          </h4>
          <button
            onClick={onClose}
            className="text-[#9ca3af] hover:text-[#dc2626] text-[14px]"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          {/* Área de Upload */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              imagePreview
                ? "border-emerald-300 bg-emerald-50/30"
                : "border-gray-300 hover:border-[#C5A059] hover:bg-amber-50/20"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleInputChange}
            />
            {imagePreview ? (
              <div className="space-y-2">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-[200px] mx-auto rounded-lg shadow-sm object-contain"
                />
                <p className="text-[10px] text-emerald-600 font-medium">
                  <i className="fas fa-check-circle mr-1" />
                  Imagem carregada. Clique para trocar.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <i className="fas fa-image text-3xl text-[#9ca3af]" />
                <p className="text-[11px] text-[#6b7280]">
                  Arraste um print de extrato/fatura ou clique para selecionar
                </p>
                <p className="text-[9px] text-[#9ca3af]">
                  Formatos: PNG, JPG, WEBP
                </p>
              </div>
            )}
          </div>

          {/* Botão OCR */}
          <button
            onClick={handleRunOcr}
            disabled={ocrStatus === "processing" || !imagePreview}
            className={`w-full px-3 py-2 text-[11px] font-bold text-white rounded-lg flex items-center justify-center gap-2 transition-all ${
              ocrStatus === "processing" || !imagePreview
                ? "bg-[#9ca3af] cursor-not-allowed"
                : "bg-[#20243B] hover:bg-[#1a1e30]"
            }`}
          >
            {ocrStatus === "processing" ? (
              <>
                <i className="fas fa-spinner fa-spin text-[10px]" />
                Processando OCR...{" "}
                {ocrProgress != null ? `${Math.round(ocrProgress * 100)}%` : ""}
              </>
            ) : (
              <>
                <i className="fas fa-magic text-[10px]" />
                Extrair dados via OCR
              </>
            )}
          </button>

          {/* Barra de Progresso */}
          {ocrStatus === "processing" && ocrProgress != null && (
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-[#C5A059] h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.round(ocrProgress * 100)}%` }}
              />
            </div>
          )}

          {/* Erro */}
          {errorMsg && (
            <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-[10px] text-red-700">
              <i className="fas fa-exclamation-circle mr-1" />
              {errorMsg}
            </div>
          )}

          {/* Texto OCR Bruto */}
          {ocrRawText && (
            <details className="border border-gray-200 rounded-lg">
              <summary className="px-3 py-1.5 text-[10px] font-semibold text-[#6b7280] cursor-pointer hover:bg-gray-50">
                <i className="fas fa-file-alt mr-1" />
                Texto bruto extraído ({ocrRawText.split("\n").length} linhas)
              </summary>
              <pre className="p-2 text-[9px] font-mono text-[#374151] bg-gray-50 rounded-b max-h-[120px] overflow-y-auto whitespace-pre-wrap">
                {ocrRawText}
              </pre>
            </details>
          )}

          {/* Preview dos Dados Extraídos */}
          {parsedRows.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-bold text-[#20243B] flex items-center gap-1.5">
                <i className="fas fa-table text-[#C5A059]" />
                Dados extraídos ({parsedRows.length} lançamentos)
              </h5>
              <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[240px] overflow-y-auto">
                <table className="w-full text-left text-[10px]">
                  <thead className="bg-[#f3f4f6] sticky top-0">
                    <tr>
                      <th className="px-2 py-1 font-semibold text-[#374151]">
                        #
                      </th>
                      <th className="px-2 py-1 font-semibold text-[#374151]">
                        Data
                      </th>
                      <th className="px-2 py-1 font-semibold text-[#374151]">
                        Valor
                      </th>
                      <th className="px-2 py-1 font-semibold text-[#374151]">
                        Descrição
                      </th>
                      <th className="px-2 py-1 font-semibold text-[#374151]">
                        Grupo
                      </th>
                      <th className="px-2 py-1 font-semibold text-[#374151]">
                        Subcat.
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parsedRows.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-2 py-1 text-[#9ca3af] text-[9px]">
                          {i + 1}
                        </td>
                        <td className="px-2 py-1 font-mono">{r.date || "—"}</td>
                        <td className="px-2 py-1 font-mono text-right">
                          {r.value != null
                            ? `R$ ${r.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "—"}
                        </td>
                        <td
                          className="px-2 py-1 max-w-[180px] truncate"
                          title={r.description}
                        >
                          {r.description || "—"}
                        </td>
                        <td className="px-2 py-1">
                          <span className="px-1.5 py-0.5 text-[8px] font-medium bg-blue-50 text-blue-700 rounded">
                            {r.group || "—"}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-[#6b7280]">
                          {r.sub || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 bg-[#f9fafb]">
          <span className="text-[10px] text-[#6b7280]">
            {parsedRows.length > 0
              ? `${parsedRows.length} lançamento${parsedRows.length !== 1 ? "s" : ""} detectado${parsedRows.length !== 1 ? "s" : ""}`
              : "Nenhum dado extraído ainda"}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1 text-[10px] font-semibold text-[#6b7280] hover:text-[#374151] rounded-md transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSendToBatch}
              disabled={parsedRows.length === 0}
              className={`px-3 py-1 text-[10px] font-bold text-white rounded-md flex items-center gap-1 transition-colors ${
                parsedRows.length === 0
                  ? "bg-[#9ca3af] cursor-not-allowed"
                  : "bg-[#C5A059] hover:bg-[#b8934a]"
              }`}
            >
              <i className="fas fa-paper-plane text-[9px]" />
              Enviar para Planilha ({parsedRows.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
